import { webRoutes } from "@/config/web/routes";
import { Button } from "@/modules/shadcn/ui/button";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGetProjectsQuery } from "../../services/projectApi";
import { CreateProjectModal } from "../CreateProjectModal";
import { ProjectCard, ProjectCardSkeleton } from "../ProjectCard";
import { EmptyProjectsState } from "./EmptyProjectsState";

export const ProjectsPage = () => {
  const { t } = useTranslation(["project", "common"]);
  const {
    data: projects,
    isLoading,
    isError,
    refetch,
  } = useGetProjectsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="flex w-full flex-1 flex-col gap-4">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <h5 className="text-xl font-semibold">{t("list.title")}</h5>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus />
          {t("list.newProject")}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex w-full flex-wrap gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </div>
      ) : isError || !projects ? (
        // A failed fetch must not read as "you have no projects" — that turned
        // every load error into a silent empty state.
        <div className="flex w-full flex-1 flex-col items-center justify-center gap-4 py-24">
          <h3 className="text-xl font-semibold">{t("list.loadFailedTitle")}</h3>
          <p className="max-w-150 text-center text-base leading-6 text-foreground/60">
            {t("list.loadFailedBody")}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t("common:actions.tryAgain")}
          </Button>
        </div>
      ) : projects.length === 0 ? (
        <EmptyProjectsState onCreate={() => setIsModalOpen(true)} />
      ) : (
        <div className="flex w-full flex-wrap gap-6">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => {
                navigate(
                  webRoutes.capture.replace(
                    ":projectId",
                    project.id.toString(),
                  ),
                );
              }}
            />
          ))}
        </div>
      )}

      {isModalOpen && (
        <CreateProjectModal onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
};
